/**
 * ChannelsPage — Integration hub for managing channel connections.
 * Task 4.11
 */
import { useEffect, useState, useCallback } from "react";
import { Plus, Wifi } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { ChannelCard } from "@/components/channels/ChannelCard";
import { AddChannelModal } from "@/components/channels/AddChannelModal";
import { useChannelStore } from "@/stores/useChannelStore";
import { useToast } from "@/components/ui/Toast";

export function ChannelsPage() {
  const { configs, loadConfigs, deleteConfig, testChannel, isLoading } =
    useChannelStore();
  const { toast } = useToast();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleTest = useCallback(
    async (configId: string) => {
      const result = await testChannel(configId);
      if (result.success) {
        toast("Channel test successful!", { variant: "success" });
      } else {
        toast(result.error ?? "Channel test failed", { variant: "error" });
      }
    },
    [testChannel, toast],
  );

  const handleRemove = useCallback(
    async (id: string) => {
      await deleteConfig(id);
      setShowDeleteConfirm(null);
      toast("Channel removed", { variant: "success" });
    },
    [deleteConfig, toast],
  );

  return (
    <div className="flex h-full flex-col" data-testid="channels-page">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Channel Integrations
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Connect and manage your messaging channels
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4" />
            Add Channel
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading && configs.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}

        {!isLoading && configs.length === 0 && (
          <EmptyState
            icon={<Wifi className="h-6 w-6" />}
            title="No channels connected"
            description="Add a messaging channel to start receiving and sending messages."
            action={
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4" />
                Add Channel
              </Button>
            }
          />
        )}

        {configs.length > 0 && (
          <div
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="channel-grid"
          >
            {configs.map((config) => (
              <ChannelCard
                key={config.id}
                config={config}
                onTest={() => handleTest(config.id)}
                onEdit={() => {
                  /* TODO: edit modal */
                }}
                onRemove={() => setShowDeleteConfirm(config.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Channel Modal */}
      <AddChannelModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="mx-4 w-full max-w-md rounded-xl border border-white/10 bg-surface-raised p-6"
            data-testid="channel-delete-confirm"
          >
            <h3 className="text-lg font-semibold text-white">
              Remove Channel
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Are you sure you want to remove this channel? You will stop
              receiving messages from it.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRemove(showDeleteConfirm)}
                loading={isLoading}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
